import { useState } from 'react';

import { enqueueSnackbar, SNACKBAR_VARIANTS } from 'components/uiLibrary/Snackbar';

import useTracking from 'components/Globals/Analytics';
import { ACTION_CALLBACK_STATUS, SECTIONS } from 'components/Globals/Analytics/constants';

import { PAYMENT_TYPES } from 'constants/subscriptions';

import { createCheckout } from 'utils/API';

import useAppContext from './useAppContext';

export const useStripeCheckout = () => {
  const { paywall } = useAppContext();
  const track = useTracking();
  const [processingProductIds, setProcessingProductIds] = useState([]);

  const trackStripeCheckout = ({ paymentType, productIds, entityType, entityId, actionStatus, errorMessage }) => {
    const trackPayload = {
      section: SECTIONS.STRIPE_CHECKOUT,
      meta: {
        id: productIds?.join(','),
        type: paymentType,
        entityType,
        entityId,
        actionStatus,
        ...(errorMessage && { errorMessage }),
      },
    };

    track.actionCallback(trackPayload);
  };

  const createCheckoutSession = async ({
    userId,
    entityId,
    entityType: originalEntityType,
    productIds,
    source,
    paymentType,
    ...restPayload
  }) => {
    const entityType = originalEntityType;

    try {
      setProcessingProductIds(productIds);

      const payload = {
        user: {
          id: userId,
        },
        entity: {
          id: entityId,
          entityType,
        },
        source: source || window.location.href,
        ...(productIds?.length && {
          lines: productIds.map(productId => ({
            product: {
              id: productId,
            },
          })),
        }),
        actionMeta: paywall?.paywallMeta,
        paymentType,
        ...restPayload,
      };

      const response = await createCheckout(payload);
      trackStripeCheckout({
        paymentType,
        productIds,
        entityType,
        entityId,
        actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
      });

      return response?.data?.session;
    } catch (err) {
      const errorMessage = err?.responseText || 'An error occurred while processing your request';
      trackStripeCheckout({
        paymentType,
        productIds,
        entityType,
        entityId,
        actionStatus: ACTION_CALLBACK_STATUS.FAILURE,
        errorMessage,
      });
      enqueueSnackbar(errorMessage, {
        variant: SNACKBAR_VARIANTS.ERROR,
      });
      return null;
    } finally {
      setProcessingProductIds([]);
    }
  };

  const handleCheckout = async ({
    userId,
    entityId,
    entityType,
    productIds,
    paymentType = PAYMENT_TYPES.INSTANT,
    ...restPayload
  } = {}) => {
    if (!productIds?.length || !entityType || !userId || !entityId) {
      const errorMessage = 'Missing required parameters for checkout';
      enqueueSnackbar(errorMessage, {
        variant: SNACKBAR_VARIANTS.ERROR,
      });
      return;
    }

    const session = await createCheckoutSession({
      userId,
      entityId,
      entityType,
      productIds,
      paymentType,
      ...restPayload,
    });

    if (!session?.url) {
      return;
    }

    window.location.href = session?.url;
  };

  const generateInvoice = async ({
    userId,
    entityId,
    entityType,
    productIds,
    paymentType = PAYMENT_TYPES.INVOICE,
    ...restPayload
  } = {}) => {
    if (!productIds?.length || !entityType || !userId || !entityId) {
      const errorMessage = 'Missing required parameters for checkout';
      enqueueSnackbar(errorMessage, {
        variant: SNACKBAR_VARIANTS.ERROR,
      });
      return false;
    }

    const session = await createCheckoutSession({
      userId,
      entityId,
      entityType,
      productIds,
      paymentType,
      ...restPayload,
    });

    if (!session?.url) {
      const errorMessage = 'Failed to create checkout session';
      enqueueSnackbar(errorMessage, {
        variant: SNACKBAR_VARIANTS.ERROR,
      });
      return false;
    }

    return true;
  };

  return {
    handleCheckout,
    generateInvoice,
    isLoading: processingProductIds?.length > 0,
    processingProductIds,
  };
};
